import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { validateEmail, validatePassword, validateRequired } from '../../utils/validators';
import { friendlyAuthError } from '../../utils/authErrors';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all';

export default function Register() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const err =
      validateRequired(name, 'Full name') ?? validateEmail(email) ?? validatePassword(password);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Creates the account, sets the display name, and sends a verification email.
      await signUp(email, password, name.trim());
      navigate('/app', { replace: true });
    } catch (e2) {
      setError(friendlyAuthError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={submit}>
      <div className="space-y-2">
        <label className="text-sm font-medium text-neutral-700">Full Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="John Doe"
          autoComplete="name"
          className={inputClass}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-neutral-700">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          autoComplete="email"
          className={inputClass}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-neutral-700">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          className={inputClass}
        />
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

      <button
        type="submit"
        id="register-button"
        disabled={submitting}
        className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? 'Creating account…' : 'Create Account'}
      </button>
      <p className="text-center text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
        By creating an account, you agree to our{' '}
        <Link to="/terms" className="text-neutral-900 dark:text-neutral-50 font-semibold hover:underline">Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" className="text-neutral-900 dark:text-neutral-50 font-semibold hover:underline">Privacy Policy</Link>.
      </p>
      <p className="text-center text-sm text-neutral-500 dark:text-neutral-400">
        Already have an account? <Link to="/auth/login" title="Sign in" id="signin-link" className="text-neutral-900 dark:text-neutral-50 font-semibold hover:underline">Sign in</Link>
      </p>
    </form>
  );
}
